import React from 'react';
import { Table, Space, Button, Tooltip, Popconfirm, Avatar, Tag, Typography } from 'antd';
import { 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CarOutlined,
  UserOutlined,
  TruckOutlined,
  ToolOutlined,
  HomeOutlined,
  FileTextOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { FieldActivity, ActivityType, ActivityStatus } from '../../../../types/field.types';
import { EmploymentType } from '../../../../types/user.types';
import { all_routes } from '../../../../router/all_routes';
import dayjs from 'dayjs';

const { Text } = Typography;

interface ActivityTableProps {
  activities: FieldActivity[];
  loading: boolean;
  isFieldWorker: boolean;
  onView: (activityId: number) => void;
  onEdit: (activity: FieldActivity) => void;
  onDelete: (id: number) => void;
  onShowLocationHistory?: (activity: FieldActivity) => void;
}

const getActivityTypeIcon = (type: ActivityType) => {
  const iconStyle = { fontSize: '16px', color: '#1890ff' };
  const icons = {
    [ActivityType.VISIT]: <UserOutlined style={iconStyle} />,
    [ActivityType.DELIVERY]: <TruckOutlined style={iconStyle} />,
    [ActivityType.MAINTENANCE]: <ToolOutlined style={iconStyle} />,
    [ActivityType.OFFICE]: <HomeOutlined style={iconStyle} />,
    [ActivityType.OTHER]: <FileTextOutlined style={iconStyle} />
  };
  return icons[type] || <FileTextOutlined style={iconStyle} />;
};

const getActivityTypeLabel = (type: ActivityType) => {
  const labels = {
    [ActivityType.VISIT]: 'Ziyaret',
    [ActivityType.DELIVERY]: 'Teslimat',
    [ActivityType.MAINTENANCE]: 'Bakım',
    [ActivityType.OFFICE]: 'Ofis',
    [ActivityType.OTHER]: 'Diğer'
  };
  return labels[type] || type;
};

const getStatusTag = (status: ActivityStatus) => {
  const config: Record<ActivityStatus, { color: string; label: string }> = {
    [ActivityStatus.PENDING_APPROVAL]: { color: 'gold', label: 'Onay Bekliyor' },
    [ActivityStatus.PLANNED]: { color: 'blue', label: 'Planlandı' },
    [ActivityStatus.COMPLETED]: { color: 'green', label: 'Tamamlandı' },
    [ActivityStatus.CANCELLED]: { color: 'default', label: 'İptal Edildi' },
    [ActivityStatus.REJECTED]: { color: 'red', label: 'Reddedildi' }
  };
  const { color, label } = config[status] || { color: 'default', label: status };
  return <Tag color={color}>{label}</Tag>;
};

const ActivityTable: React.FC<ActivityTableProps> = ({ 
  activities, 
  loading, 
  isFieldWorker,
  onView, 
  onEdit, 
  onDelete,
  onShowLocationHistory
}) => {
  const routes = all_routes;

  const baseColumns = [
    {
      title: 'Başlık',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: FieldActivity) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div>{getActivityTypeIcon(record.type)}</div>
          <div>
            <div style={{ fontWeight: '500' }}>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {getActivityTypeLabel(record.type)}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: ActivityStatus) => getStatusTag(status)
    },
    {
      title: 'Atanan Kişi',
      dataIndex: 'assignedUsers',
      key: 'assignedUsers',
      render: (assignedUsers: any[]) => {
        const user = assignedUsers?.[0];
        if (!user) return <Text type="secondary">-</Text>;
        
        return (
          <Link 
            to={routes.employeedetails.replace(':id', user.id.toString())}
            style={{ textDecoration: 'none' }}
          >
            <Space>
              <Avatar style={{ backgroundColor: '#87d068' }}>
                {user.firstName[0]}{user.lastName[0]}
              </Avatar>
              <Text style={{ color: '#1890ff', cursor: 'pointer' }}>
                {user.firstName} {user.lastName}
              </Text>
            </Space>
          </Link>
        );
      }
    }
  ];

  // Saha çalışanı ise araç kolonunu ekle
  const vehicleColumn = isFieldWorker ? [{
    title: 'Araç',
    dataIndex: 'vehicle',
    key: 'vehicle',
    render: (vehicle: any) => (
      vehicle ? (
        <Tooltip title={`Plaka: ${vehicle.plateNumber}`}>
          <Space size="small">
            <CarOutlined style={{ color: '#1890ff' }} />
            <Text>{vehicle.name}</Text>
            {vehicle.assignmentType === 'pool' && <Tag color="purple">Havuz</Tag>}
          </Space>
        </Tooltip>
      ) : (
        <Text type="secondary">-</Text>
      )
    )
  }] : [];

  const dateColumn = {
    title: 'Tarih Aralığı',
    key: 'dates',
    render: (record: FieldActivity) => {
      const startDate = dayjs(record.startDate);
      const endDate = dayjs(record.endDate);
      const isSameDay = startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD');
      
      if (isSameDay) {
        // Aynı gün: Tarih ve saat göster
        return (
          <div>
            <div>{startDate.format('DD.MM.YYYY')}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {startDate.format('HH:mm')} - {endDate.format('HH:mm')}
            </Text>
          </div>
        );
      } else {
        // Farklı günler: Sadece tarihler
        return (
          <div>
            <div>{startDate.format('DD.MM.YYYY')}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {endDate.format('DD.MM.YYYY')}
            </Text>
          </div>
        );
      }
    }
  };

  // Saha çalışanı ise toplam masraf kolonunu ekle
  const expenseColumn = isFieldWorker ? [{
    title: 'Toplam Masraf',
    dataIndex: 'totalExpenses',
    key: 'totalExpenses',
    render: (amount: number) => (
      <Text strong style={{ color: amount > 0 ? '#ff4d4f' : '#8c8c8c' }}>
        {amount > 0 ? `₺${amount.toFixed(2)}` : '-'}
      </Text>
    )
  }] : [];

  const actionsColumn = {
    title: 'İşlemler',
    key: 'actions',
    render: (record: FieldActivity) => (
      <Space size="small">
        <Tooltip title="Detaylar">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => onView(record.id)}
          />
        </Tooltip>
        {onShowLocationHistory && (
          <Tooltip title="Konum Geçmişi">
            <Button
              type="text"
              icon={<EnvironmentOutlined />}
              onClick={() => onShowLocationHistory(record)}
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
        )}
        <Tooltip title="Düzenle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
        </Tooltip>
        <Popconfirm
          title="Bu aktiviteyi silmek istediğinizden emin misiniz?"
          onConfirm={() => onDelete(record.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Tooltip title="Sil">
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      </Space>
    )
  };

  const columns = [
    ...baseColumns,
    ...vehicleColumn,
    dateColumn,
    ...expenseColumn,
    actionsColumn
  ];

  return (
    <Table
      columns={columns}
      dataSource={activities}
      rowKey="id"
      loading={loading}
      pagination={{
        pageSize: 10,
        showTotal: (total) => `Toplam ${total} aktivite`
      }}
    />
  );
};

export default ActivityTable;

